import { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import Component from '@glimmer/component'
import RouteTemplate from 'ember-route-template'
import FluencySprintsStudentResults, {
  type LessonItem,
  type StudentItem,
} from 'teacher-ui-client/components/tables/fluency-sprints/student-results'
import type MathsFluencySprintsTeacherStudentResultsController from 'teacher-ui-client/reporting/ui-scope/maths/fluency-sprints/teacher/student-results/controller'
import type MathsFluencySprintsTeacherStudentResultsRoute from 'teacher-ui-client/reporting/ui-scope/maths/fluency-sprints/teacher/student-results/route'
import type { MentalMinuteStudentLessonItem } from 'teacher-ui-client/utils/student-events/fetchers/mental-minute-student'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'

interface Signature {
  Args: {
    model: ModelFor<MathsFluencySprintsTeacherStudentResultsRoute>
    controller: MathsFluencySprintsTeacherStudentResultsController
  }
}

function findHighestLessonPassed(lessons: MentalMinuteStudentLessonItem[]): Nullable<MentalMinuteStudentLessonItem> {
  let highestLessonPassed: Nullable<MentalMinuteStudentLessonItem> = null
  lessons.forEach((lesson) => {
    if (lesson.lesson_mastered && (!highestLessonPassed || lesson.lesson > highestLessonPassed.lesson)) {
      highestLessonPassed = lesson
    }
  })

  return highestLessonPassed
}

export class UiScopeMathsFluencySprintsTeacherStudentResultsPage extends Component<Signature> {
  @service lessonInfo!: LessonInfoService

  /**
   * Construct an array of all mental minute lessons. Each lesson object should contain the lesson id, name, category
   * and category name. This is used for the right side table of the student results component
   *
   * Due to the way lesson data is loaded, we need to use the category from intl to get the category id.
   */
  get allLessons(): LessonItem[] {
    const categoryNameMapping = {
      Addition: 'addition',
      Subtraction: 'subtraction',
      Multiplication: 'multiplication',
      Division: 'division',
    }

    return this.lessonInfo
      .lessonsFor(Precinct.MS_MENTAL_MINUTE)
      .map((lesson) => {
        const lessonId = lesson.lesson_number
        const categoryName = lesson.category
        const lessonName = lesson.name
        if (typeof lessonId !== 'number' || !categoryName || !lessonName) return null

        const isCategoryName =
          categoryName === 'Addition' ||
          categoryName === 'Subtraction' ||
          categoryName === 'Multiplication' ||
          categoryName === 'Division'

        const category = isCategoryName ? categoryNameMapping[categoryName] : ''

        return { lessonId, lessonName, category, categoryName }
      })
      .filter(isPresent)
  }

  get students(): StudentItem[] {
    const { mentalMinuteStudent, scopedStudents } = this.args.model
    if (!mentalMinuteStudent) return []

    return mentalMinuteStudent.students
      .map((studentData) => {
        const studentRecord = scopedStudents.find((student) => student.id === String(studentData.student_id))
        if (!studentRecord) return null

        const highestLessonPassed = findHighestLessonPassed(studentData.lessons)

        const lessonId = highestLessonPassed ? highestLessonPassed.lesson : 0
        const lessonName = highestLessonPassed
          ? (this.lessonInfo.lessonsFor(Precinct.MS_MENTAL_MINUTE).find((lesson) => lesson.lesson_number === lessonId)
              ?.name ?? '')
          : ''
        const lessonLabel = lessonId ? `${lessonId}: ${lessonName}` : ''

        return {
          studentId: studentData.student_id,
          firstName: studentRecord.firstName,
          lastName: studentRecord.lastName,
          externalId: studentRecord.externalId,
          highestLessonPassed: {
            id: lessonId,
            name: lessonName,
            label: lessonLabel,
          },
          lessons: studentData.lessons,
        }
      })
      .filter(isPresent)
  }

  <template>
    <FluencySprintsStudentResults
      @students={{this.students}}
      @allLessons={{this.allLessons}}
      @studentRouteName="reporting.ui-scope.maths.fluency-sprints.student.table"
      @onSortClick={{@controller.updateSorts}}
      @onCategoryClick={{@controller.updateCategory}}
      @sortingConfig={{@controller.sortingConfig}}
      @paginationConfig={{@controller.paginationConfig}}
      @category={{@controller.category}}
      @exportable={{true}}
    />
  </template>
}

export default RouteTemplate(UiScopeMathsFluencySprintsTeacherStudentResultsPage)
